import {
  queryOptions,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { uniq } from 'lodash-es';

import {
  createExcelExport,
  createPowerPointExport,
  createRawDataExport,
  createSingleSurveyDetailExport,
  createSPSSExport,
  deleteSavedExport,
  downloadExport,
  editExport,
  fetchExports,
  saveExportBanners,
} from 'services/backend/exports';
import { downloadFile } from 'util/files';
import { fetchQuestionsForSurvey } from 'services/backend/questions';
import { fetchSurvey } from 'services/backend/surveys';
import { fetchSurveyAudienceTargetBySurveyId } from 'services/backend/surveyAudienceTarget';
import {
  getAudienceGroups,
  getAudienceGroupOptions,
} from 'services/backend/audienceGroup';
import {
  formDataToApiExcelExportData,
  formDataToApiPowerPointExportData, // eslint-disable-line @typescript-eslint/no-unused-vars
  getInitialExportFormData,
} from 'util/exports';
import { generateQuestionnaireDocx, SurveyTargetForWord } from 'util/docx';
import { getQuestionOptions } from 'util/formOptions';
import { questionQueries } from './questions';
import { ReactQueryFunctionCallbacks } from 'types/internal';
import { savedCrosstabsQueries } from './savedCrosstabs';
import { showErrorMessage } from 'util/notifications';
import {
  AudienceGroup,
  AudienceGroupOption,
  Survey,
  SurveyAudienceBody,
} from 'types/domainModels';
import {
  AUDIENCE_TARGET_TYPE,
  logicalModifierOptionsForTargetEnum,
  logicalModifierOptionsForTargetRange,
  pairifyNumberRange,
} from 'util/demographics';

export const exportQueries = {
  all: () => ['exports'],

  surveyExports: ({
    showArchived,
    surveyHash,
  }: {
    showArchived: boolean;
    surveyHash: string | undefined;
  }) =>
    queryOptions({
      queryKey: [...exportQueries.all(), surveyHash, showArchived],
      queryFn: surveyHash
        ? () => fetchExports({ showArchived, surveyHash })
        : skipToken,
      enabled: !!surveyHash,
    }),
};

export function useCreateExcelExport(
  opts?: ReactQueryFunctionCallbacks<typeof createExcelExport>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: createExcelExport,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: exportQueries.all() });

      opts?.onSuccess?.(...args);
    },
  });
}

export function useCreatePowerPointExport(
  opts?: ReactQueryFunctionCallbacks<typeof createPowerPointExport>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: createPowerPointExport,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: exportQueries.all() });

      opts?.onSuccess?.(...args);
    },
  });
}

export function useCreateRawDataExport(
  opts?: ReactQueryFunctionCallbacks<typeof createRawDataExport>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: createRawDataExport,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: exportQueries.all() });

      opts?.onSuccess?.(...args);
    },
  });
}

export function useCreateSPSSExport(
  opts?: ReactQueryFunctionCallbacks<typeof createSPSSExport>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: createSPSSExport,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: exportQueries.all() });

      opts?.onSuccess?.(...args);
    },
  });
}

export function useDeleteSavedExport(
  opts?: ReactQueryFunctionCallbacks<
    typeof deleteSavedExport,
    { surveyId: number }
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: deleteSavedExport,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: savedCrosstabsQueries.forSurvey({
          surveyId: args[1].surveyId,
        }),
      });

      opts?.onSuccess?.(...args);
    },
  });
}

export function useDownloadExport({
  filename,
  ...rest
}: { filename: string } & ReactQueryFunctionCallbacks<typeof downloadExport>) {
  return useMutation({
    ...rest,
    mutationFn: downloadExport,
    onError: (err) => {
      showErrorMessage({ err });
    },
    onSuccess: (response) => {
      downloadFile({
        contentType: response.headers['content-type'],
        data: response.data,
        filename,
      });
    },
  });
}

function getSurveyQuotasFromTargets(
  audienceTargets: SurveyAudienceBody[],
  allAudienceGroups: AudienceGroup[] | undefined,
  audienceGroupOptionsByGroupId: Map<number, AudienceGroupOption[]>,
): SurveyTargetForWord[] {
  if (audienceTargets.length === 0) {
    return [];
  }
  const surveyTargets: SurveyTargetForWord[] = [];

  audienceTargets.forEach((target) => {
    const foundTargetIndex = surveyTargets.findIndex(
      (existingTarget) =>
        existingTarget.audienceGroupId === target.audienceGroupId,
    );
    const audienceGroupOptionsResponse =
      audienceGroupOptionsByGroupId.get(target.audienceGroupId) ?? [];
    const surveyTarget: SurveyTargetForWord = {
      definitions: [],
      questionTitle:
        allAudienceGroups?.find(
          (group: AudienceGroup) => group.id === target.audienceGroupId,
        )?.name || '',
      audienceGroupId: target.audienceGroupId,
    };
    const modifier = (
      target.audienceGroupId === AUDIENCE_TARGET_TYPE.RANGE
        ? logicalModifierOptionsForTargetRange
        : logicalModifierOptionsForTargetEnum
    ).find((singleTarget) => singleTarget.value === target.logicalModifier);

    let options: string;
    if (target.audienceGroupId === AUDIENCE_TARGET_TYPE.RANGE) {
      options = pairifyNumberRange(target.numberRange)
        .map(({ start, end }) => `${start} - ${end}`)
        .join(' or ');
    } else if (target.audienceGroupId === AUDIENCE_TARGET_TYPE.OPEN_END) {
      options = target.numberSelections.join(' or ');
    } else {
      options = target.audienceGroupOptionSelections
        .map((groupOptionSelectionId) =>
          audienceGroupOptionsResponse.find(
            (singleOption: AudienceGroupOption) =>
              singleOption.id === groupOptionSelectionId,
          ),
        )
        .map((option) => option?.name)
        .join(' or ');
    }

    if (foundTargetIndex > -1) {
      surveyTargets[foundTargetIndex].definitions.push(
        `${Number(target.percentage) * 100}% ${modifier?.label} ${options}`,
      );
    } else {
      surveyTarget.definitions.push(
        `${Number(target.percentage) * 100}% ${modifier?.label} ${options}`,
      );
    }

    if (surveyTarget.definitions?.length) {
      surveyTargets.push(surveyTarget);
    }
  });
  return surveyTargets;
}

export function useDownloadQuestionnaire({ surveyId }: { surveyId: number }) {
  return useMutation({
    mutationFn: async ({
      includeInactiveQuestions,
      showTargetByGeoFeature,
    }: {
      includeInactiveQuestions: boolean;
      showTargetByGeoFeature: boolean;
    }) => {
      const [survey, questions, targets, audienceGroups] = await Promise.all([
        fetchSurvey({ surveyId }),
        fetchQuestionsForSurvey({ surveyId }),
        fetchSurveyAudienceTargetBySurveyId({ surveyId }),
        getAudienceGroups(),
      ]);

      const audienceGroupIds = uniq(
        targets.map((target) => target.audienceGroupId),
      );

      const audienceGroupOptionsQueries = await Promise.all(
        audienceGroupIds.map((id) =>
          getAudienceGroupOptions({ audienceGroupId: id }),
        ),
      );
      const audienceGroupOptionsByGroupId = new Map<
        number,
        AudienceGroupOption[]
      >(
        audienceGroupIds.map((audienceGroupId, index) => [
          audienceGroupId,
          audienceGroupOptionsQueries[index] ?? [],
        ]),
      );

      const targetsToDisplay = getSurveyQuotasFromTargets(
        targets,
        audienceGroups,
        audienceGroupOptionsByGroupId,
      );

      generateQuestionnaireDocx({
        isMaster: includeInactiveQuestions,
        passedQuestions: questions,
        survey,
        targetsToDisplay,
        showTargetByGeoFeature,
      });
    },
    onError: (err) => {
      showErrorMessage({ err });
    },
  });
}

export function useEditExport(
  opts?: ReactQueryFunctionCallbacks<typeof editExport>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: editExport,
    onSuccess(...args) {
      queryClient.invalidateQueries({ queryKey: exportQueries.all() });

      opts?.onSuccess?.(...args);
    },
  });
}

export function useGenerateSummary(opts: {
  onError(err: Error): void;
  onSuccess(): void;
  surveyId: number;
}) {
  const queryClient = useQueryClient();

  const { refetch: fetchSurveyQuestions } = useQuery(
    questionQueries.forSurvey({ surveyId: opts.surveyId }),
  );

  const { mutateAsync: createExcelExport } = useCreateExcelExport();

  return useMutation({
    ...opts,
    mutationFn: async ({
      survey,
      waveIds,
    }: {
      survey: Survey;
      waveIds: number[];
    }) => {
      const { data } = await fetchSurveyQuestions();
      const questions = getQuestionOptions({ questions: data ?? [] });

      const exportFormData = getInitialExportFormData({ questions });
      // Not entirely sure why version 1 of the code set "stackQuestionLabels" to false but it
      // did and currently don't have the bandwidth to dig in to this so keeping the functionality
      // here the same as v1.
      exportFormData.features.stackQuestionLabels = false;
      exportFormData.waveIds = waveIds;

      createExcelExport({
        data: formDataToApiExcelExportData({
          formData: exportFormData,
        }),
        surveyId: survey.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: exportQueries.all() });

      opts?.onSuccess?.();
    },
  });
}

export function useSaveExportBanners(
  opts?: ReactQueryFunctionCallbacks<typeof saveExportBanners>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: saveExportBanners,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: savedCrosstabsQueries.forSurvey({
          surveyId: args[1].surveyId,
        }),
      });

      opts?.onSuccess?.(...args);
    },
  });
}

export const useCreateSingleSurveyDetailExport = ({
  onError,
  onSuccess,
}: ReactQueryFunctionCallbacks<typeof createSingleSurveyDetailExport> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSingleSurveyDetailExport,
    onError,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: exportQueries.all() });
      onSuccess?.(...args);
    },
  });
};
